import React, { useState, useEffect, useRef } from 'react'
import Modal from 'aldoo-ra/Modal'
import Button from 'aldoo-ra/Button' // Replace with your actual Button import
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import Tooltip from 'aldoo-ra/Tooltip'
import { showNotification } from 'aldoo-ra/Notification'

const LocalizationKeyDialog = ({ isOpen, onClose, controller }) => {
  const [localizationText, setLocalizationText] = useState('')
  const [errorMessage, setErrorMessage] = useState() // State to track errors
  const textareaRef = useRef(null) // Create a ref for the textarea

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      if (!controller.primaryLanguage) {
        showNotification({
          className: 'bg-admin_error text-white',
          message: (
            <span className="font-bold">
              Please set a Primary Language first
            </span>
          ),
        })
        onClose()
        return
      }

      textareaRef.current.focus() // Focus the textarea when the modal opens
    }
  }, [isOpen])

  const checkForError = (input) => {
    let lineCount = 0
    let errorAtLine = ''
    const lines = input.split('\n')

    const keys = []

    lines.forEach((line) => {
      lineCount++
      // Match the key and value in the format: key [[value]]
      const match = line.match(/^([^\s]+)\s*\[\[(.+?)\]\]$/)
      if (!match)
        return (errorAtLine = `Invalid key [[value]] format at line: ${lineCount}`)

      const key = match[1].trim() // Extract the key
      if (~keys.indexOf(key))
        return (errorAtLine = `Duplicate key at line: ${lineCount}`)
      keys.push(key)
    })
    return errorAtLine
  }

  const handleSave = () => {
    if (!localizationText.trim()) {
      setErrorMessage('Please enter at least one key or key,value')
      return
    }

    const errorAtLine = checkForError(localizationText.trim())

    if (errorAtLine) return setErrorMessage(errorAtLine)

    const lines = localizationText.trim().split('\n') // Split the textarea input by new lines
    // Split the textarea input by new lines
    lines.forEach((line) => {
      const match = line.match(/^([^\s]+)\s*\[\[(.+?)\]\]$/)
      if (!match) return

      const key = match[1].trim() // Extract the key
      const value = match[2].trim() // Extract the value inside [[ ]]

      const payload = {
        key,
        type: 'text',
        language: controller.primaryLanguage,
      }

      if (value) payload.content = value

      // Add the key and content to the controller
      controller.addLocalizationKey(payload)
    })

    setLocalizationText('') // Clear the input after adding
    setErrorMessage('') // Clear any previous error messages
    onClose() // Close the modal
  }

  // Reset the error message when the textarea content changes
  const handleInputChange = (e) => {
    setLocalizationText(e.target.value)
    if (errorMessage) {
      setErrorMessage('') // Clear the error message when typing
    }
  }

  return (
    <Modal
      variant="admin"
      title="Localization Key"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape={true}
      className="w-full md:w-[900px] sm:w-[500px] md:absolute h-full md:h-auto sm:max-w-[960px]"
    >
      <div className="p-4 relative">
        <textarea
          ref={textareaRef} // Attach the ref to the textarea
          className={`text-admin_text w-full h-[200px] p-2 border border-admin_neutral_1 bg-admin_canvas focus:outline-none`}
          placeholder="key, value (new line for each pair)"
          value={localizationText}
          onChange={handleInputChange}
        ></textarea>
        <div className="flex items-center mb-2">
          <Tooltip
            content={
              <div className="text-left">
                <div>Type key, value to define new keys.</div>
                <div>You can add multiple keys, one per lines</div>
                <code className="mt-4">
                  // Example
                  <br />
                  my_key1 [[My Value1]]
                  <br />
                  my_key2 [[My Value2]]
                </code>
              </div>
            }
            position="right"
          >
            <InformationCircleIcon className="w-5 h-5 text-admin_text_secondary cursor-pointer" />
          </Tooltip>
        </div>

        <div className="flex justify-end mt-4">
          <Button 
            variant="admin_primary" 
            onClick={handleSave}
          >
            Add Key
          </Button>
        </div>

        {/* Error message positioned at the bottom-left corner */}
        {errorMessage && (
          <div className="absolute bottom-2 left-2 text-admin_error text-sm">
            {errorMessage}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default LocalizationKeyDialog
